import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayPicker, type ChevronProps, type DayPickerProps } from 'react-day-picker'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

/**
 * A month grid, for picking one day.
 *
 * Wraps `react-day-picker` the way the rest of `components/ui` wraps Radix: the
 * behaviour — keyboard grid, ARIA roles, disabled ranges, month navigation —
 * comes from the library, and everything visual is this file's business.
 *
 * **Styled entirely through `classNames`, with the library's own stylesheet
 * deliberately not imported.** `react-day-picker/style.css` is global CSS that
 * would sit outside the Tailwind layers and theme tokens every other control
 * here is built from, and it ships its own colours for selected/today/disabled
 * that would need overriding one by one. The grid itself is a real `<table>`, so
 * it lays out without that stylesheet; only the paint is ours to supply.
 *
 * Class names are the v10 slot keys (`month_grid`, `day_button`, `weekday`) —
 * they were renamed from the v8 ones most shadcn snippets still use, so a
 * copied-in override silently does nothing. See `UI` in the package's types.
 */
export function Calendar({ className, classNames, showOutsideDays = true, ...props }: DayPickerProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('w-fit', className)}
      classNames={{
        // `relative` is load-bearing: `nav` below is absolutely positioned, and
        // without a positioned ancestor here it anchors to whatever container
        // the calendar is dropped into — sending the arrows to the corners of
        // the popover instead of onto the caption row.
        months: 'relative flex flex-col gap-4',
        month: 'flex flex-col gap-2',
        month_caption: 'flex h-7 items-center justify-center',
        caption_label: 'text-sm font-medium',
        nav: 'absolute inset-x-0 top-0 flex h-7 items-center justify-between px-0',
        button_previous: cn(
          buttonVariants({ variant: 'outline', size: 'icon' }),
          'h-7 w-7 bg-transparent p-0 disabled:opacity-40',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline', size: 'icon' }),
          'h-7 w-7 bg-transparent p-0 disabled:opacity-40',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'w-8 text-[11px] font-normal text-muted-foreground',
        week: 'mt-1 flex w-full',
        // The cell is the hit area and the button fills it, so there is no
        // 1px gutter between a selected day and the pointer that missed it.
        day: 'h-8 w-8 p-0 text-center text-xs',
        day_button: cn(
          'h-8 w-8 rounded-md font-normal tabular-nums transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:pointer-events-none disabled:opacity-40',
        ),
        selected:
          '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground',
        // Today is a ring, never a fill: a filled today next to a filled
        // selection reads as two selected days.
        today: '[&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-primary/40',
        outside: '[&>button]:text-muted-foreground/50',
        disabled: '[&>button]:text-muted-foreground/40',
        hidden: 'invisible',
        ...classNames,
      }}
      // `nav` is absolutely positioned over the caption row, so the month name
      // stays centred whatever its length instead of being pushed by arrows.
      components={{ Chevron: NavChevron }}
      {...props}
    />
  )
}

/**
 * The library's default chevron is an inline SVG of its own; this swaps in the
 * icon set the rest of the app uses. Only the two horizontal orientations occur
 * here — there is no dropdown navigation — but `up`/`down` are handled rather
 * than left to fall through as a left arrow.
 */
function NavChevron({ orientation, className }: ChevronProps) {
  const Icon = orientation === 'left' ? ChevronLeft : ChevronRight
  return <Icon className={cn('h-4 w-4', className)} aria-hidden />
}
